import React, { createContext, useState, useEffect, useCallback } from 'react';

// 앱의 전역 상태를 담을 Context를 생성합니다.
export const AppContext = createContext();

// Context를 통해 상태를 제공할 Provider 컴포넌트입니다.
export const AppProvider = ({ children }) => {
    // 1. 상태(State) 정의
    const [theme, setTheme] = useState('light');
    const [fontSize, setFontSize] = useState(16);
    // [New Feature #4] 클라이언트 전용 설정 상태 추가
    const [settings, setSettings] = useState({
        enableAnimations: true, // 스켈레톤 UI 등 애니메이션 효과
        showPersonaTags: true, // (괴짜): (연구원): 이름표 표시 여부
    });

    // 2. 앱 첫 실행 시 설정 불러오기 (초기화)
    useEffect(() => {
        // 테마 불러오기
        const savedTheme = localStorage.getItem('ego-coach-theme') || 'light';
        setTheme(savedTheme);

        // 폰트 크기 불러오기
        const savedFontSize = parseInt(localStorage.getItem('ego-coach-fontSize'), 10) || 16;
        setFontSize(savedFontSize);

        // [New Feature #4] 개인 설정 불러오기
        const savedSettings = localStorage.getItem('ego-coach-settings');
        if (savedSettings) {
            setSettings(JSON.parse(savedSettings));
        }
    }, []); // 빈 배열: 이 코드는 앱이 처음 실행될 때 단 한 번만 실행됩니다.

    // 3. 테마 상태가 변경될 때마다 실제 디자인(CSS)에 적용
    useEffect(() => {
        document.body.className = ''; // 기존 테마 클래스를 모두 삭제 (충돌 방지)
        document.body.classList.add(theme + '-theme');
    }, [theme]);

    // 4. 테마 변경 함수
    const toggleTheme = useCallback(() => {
        setTheme(prevTheme => {
            const newTheme = prevTheme === 'light' ? 'dark' : 'light';
            localStorage.setItem('ego-coach-theme', newTheme);
            return newTheme;
        });
    }, []);

    // 5. 폰트 크기 변경 함수
    const handleFontSizeChange = useCallback((direction) => {
        setFontSize(prevSize => {
            const newSize = direction === 'increase' ? prevSize + 1 : prevSize - 1;
            if (newSize >= 12 && newSize <= 24) { // 폰트 크기 제한
                localStorage.setItem('ego-coach-fontSize', newSize);
                return newSize;
            }
            return prevSize;
        });
    }, []);

    // [New Feature #4] 개인 설정 업데이트 함수
    const updateSettings = useCallback((newSetting) => {
        setSettings(prevSettings => {
            const updatedSettings = { ...prevSettings, ...newSetting };
            localStorage.setItem('ego-coach-settings', JSON.stringify(updatedSettings));
            return updatedSettings;
        });
    }, []);


    // 6. 자식 컴포넌트들에게 상태와 함수들을 제공
    const providerValue = {
        theme,
        toggleTheme,
        fontSize,
        handleFontSizeChange,
        settings,
        updateSettings
    };

    return (
        <AppContext.Provider value={providerValue}>
            {children}
        </AppContext.Provider>
    );
};
